import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { ConvertDate } from '../shared/date/convertDate';
import { TokenParser } from '../shared/auth/token-parser';
import { AccountService } from '../shared/account/account.service';
import { LocalStorageService } from '../shared/local-storage/local-storage.service';
import { MsalBroadcastService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import {
  filter,
  takeUntil,
  Subject,
} from 'rxjs';
import {
  msalConfig,
} from '../auth.config';

@Component({
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  convertDate: ConvertDate;
  tokenParser: TokenParser;
  signedInDisplay: boolean = false;
  additionalState: string;
  refreshToken: string;
  accessToken: string;
  accessTokenExpiry: string;
  errorMessage: string;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private accountService: AccountService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private msalBroadcastService: MsalBroadcastService)
  {
    this.convertDate = new ConvertDate();
    this.tokenParser = new TokenParser();
  }

  ngOnInit() {
    this.setSignedInDisplay();

    this.route.params.subscribe(p => {
      const errorMessage: string = p['errorMessage'];
      if (errorMessage !== undefined 
          && errorMessage !== ''
          && !errorMessage.startsWith('access_denied: AADB2C90091: The user has cancelled entering self-asserted information.')) {
        this.errorMessage = errorMessage;
      }

      this.router.navigate(
        ["/home"],
        {
          relativeTo: this.route,
        });
    });

    this.subscribeToMsalSuccessEvents();
    this.subscribeToMsalInProgressEvents();

    if (this.signedInDisplay) {
      this.accountService.acquireTokenSilent();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  refresh() {
    this.accountService.refreshTokens();
  }

  private subscribeToMsalSuccessEvents(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
          || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          || msg.eventType === EventType.SSO_SILENT_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.updateFieldsOnPage(payload);
      });
  }

  private subscribeToMsalInProgressEvents() {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setSignedInDisplay();
      });
  }

  private setSignedInDisplay(): void {
    this.signedInDisplay = this.accountService.accountExists();
  }

  private updateFieldsOnPage(authPayload: AuthenticationResult) {
    this.additionalState = this.localStorageService.getAdditionalState();

    this.accessToken = authPayload.accessToken;
    if (this.accessToken) {
      const accessTokenClaims = this.tokenParser.ParseTokenClaims(this.accessToken);
      this.accessTokenExpiry = this.convertDate.ConvertFromEpochString(accessTokenClaims['exp']);
    }

    // extract refresh token from the session storage
    const msalTokensCacheKey = this.findTokenKeysCacheKey(msalConfig.auth.clientId);
    const tokenKeys = msalTokensCacheKey ? sessionStorage.getItem(msalTokensCacheKey) : null;

    if (tokenKeys !== null) {
      const refreshTokenKey = JSON.parse(tokenKeys).refreshToken;
      const refreshTokenString = sessionStorage.getItem(refreshTokenKey);
      if (refreshTokenString != null) {
        const refreshTokenObject = JSON.parse(refreshTokenString);
        this.refreshToken = refreshTokenObject.secret;
      }      
    }
  }

  private findTokenKeysCacheKey(clientId: string): string | null {
    // The regex pattern matches MSAL's token keys cache key formats:
    // - msal.token.keys.{clientId} (format without version number)
    // - msal.{number}.token.keys.{clientId} (format with version number)
    const pattern = new RegExp(`^msal(\\.\\d+)?\\.token\\.keys\\.${clientId}$`);
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && pattern.test(key)) {
        return key;
      }
    }
    
    return null;
  }
}
